import { FC, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { GridRow, GridRowProps } from "@mui/x-data-grid";
import { DataTable, useDataTableState } from "@/components/ui/DataTable";
import { SearchToggle } from "@/components/ui/SearchToggle";
import { useSnackbar } from "@/components/Snackbar";
import { useDialog } from "@/hooks/useDialog";
import { fetchSegments, deleteSegment } from "@/apis/bacchus/segment";
import {
  Segment,
  SegmentFilterBy,
  SegmentListResponse,
} from "@/apis/bacchus/types";
import { useDebounce } from "@/hooks/useDebounce";
import { SegmentPageLayout } from "../components/SegmentPageLayout";
import { SegmentsToolbar } from "./SegmentsToolbar";
import { getColumns } from "./columns";
import { SegmentModificationDialogs } from "@/components/Segment/SegmentModificationDialogs";
import {
  SegmentsEmptyState,
  SegmentsSearchEmptyState,
  SegmentsErrorState,
} from "./SegmentsEmptyState";

// Row slot that adds a stable data-testid per segment row for e2e targeting
const SegmentRow = (props: GridRowProps) => (
  <GridRow {...props} data-testid={`segment-row-${props.rowId}`} />
);

export const SegmentsListPage: FC = () => {
  // Table state managed by hook
  const tableState = useDataTableState({
    initialPageSize: 50,
  });

  // Filter and search state
  const [filterBy, setFilterBy] = useState<SegmentFilterBy>("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 700);

  // Data fetching state
  const [data, setData] = useState<SegmentListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Preserve rowCount during loading to maintain pagination stability
  const [lastRowCount, setLastRowCount] = useState<number | undefined>(
    undefined
  );

  // Dialog state
  const deleteDialog = useDialog<Segment>();
  const editDialog = useDialog<Segment>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Snackbar for feedback
  const { showSuccess, showError } = useSnackbar();

  // Navigation
  const navigate = useNavigate();

  // Reset page when filter or search changes
  useEffect(() => {
    tableState.resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy, debouncedSearch]);

  // Fetch segments function
  const loadSegments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // API uses 1-indexed pages, DataGrid uses 0-indexed
      const result = await fetchSegments({
        page: tableState.paginationModel.page + 1,
        pageSize: tableState.paginationModel.pageSize,
        filterBy,
        search: debouncedSearch || undefined,
      });

      setData(result);
      if (result.meta?.total_count !== undefined) {
        setLastRowCount(result.meta.total_count);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch segments")
      );
    } finally {
      setLoading(false);
    }
  }, [
    tableState.paginationModel.page,
    tableState.paginationModel.pageSize,
    filterBy,
    debouncedSearch,
  ]);

  // Fetch segments on mount and when dependencies change
  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  // Handlers
  const handleFilterByChange = useCallback((newFilterBy: SegmentFilterBy) => {
    setFilterBy(newFilterBy);
  }, []);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearch(newSearch);
  }, []);

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialog.data?.id) return;

    setIsDeleting(true);
    try {
      await deleteSegment(deleteDialog.data.id);
      deleteDialog.close();
      showSuccess("Segment deleted successfully");
      loadSegments();
    } catch (err) {
      console.error("Failed to delete segment:", err);
      showError("Failed to delete segment. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDialog, showSuccess, showError, loadSegments]);

  // Edit handler
  const handleEditSave = useCallback(() => {
    editDialog.close();
    loadSegments();
  }, [editDialog, loadSegments]);

  // Create handler
  const handleCreateClick = useCallback(() => {
    navigate("/segments/explore?action=new");
  }, [navigate]);

  // Row click handler - navigate to segment edit page
  const handleRowClick = useCallback(
    (row: Segment) => {
      if (row.id) {
        navigate(`/segments/${row.id}`);
      }
    },
    [navigate]
  );

  // Columns definition
  const columns = useMemo(
    () => getColumns(deleteDialog.open, editDialog.open),
    [deleteDialog.open, editDialog.open]
  );

  // Rows - return empty array during loading to prevent old data showing beneath skeletons
  const rows = useMemo(() => {
    if (loading) return [];
    return data?.data || [];
  }, [data?.data, loading]);

  // Row count for pagination
  const rowCount = loading ? lastRowCount : (data?.meta?.total_count ?? 0);

  return (
    <SegmentPageLayout
      title="Segments"
      action={
        <Box display="flex" alignItems="center" gap={2}>
          <Box id="segments-search-input">
            <SearchToggle
              searchTerm={search}
              onSearchChange={handleSearchChange}
              placeholder="Search segments..."
              width={250}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            id="segments-create-btn"
            data-testid="segment-create-btn"
          >
            Create New Segment
          </Button>
        </Box>
      }
    >
      <SegmentsToolbar
        filterBy={filterBy}
        onFilterByChange={handleFilterByChange}
      />

      <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Box id="segments-table" sx={{ height: "100%" }}>
            <DataTable<Segment>
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id || ""}
              rowCount={rowCount ?? 0}
              paginationModel={tableState.paginationModel}
              onPaginationModelChange={tableState.onPaginationModelChange}
              loading={loading}
              error={error ?? undefined}
              onRowClick={handleRowClick}
              slots={{
                row: SegmentRow,
                empty: debouncedSearch
                  ? SegmentsSearchEmptyState
                  : SegmentsEmptyState,
                error: SegmentsErrorState,
              }}
            />
          </Box>
        </Box>
      </Box>

      <SegmentModificationDialogs
        deleteDialog={deleteDialog}
        editDialog={editDialog}
        onDelete={handleConfirmDelete}
        onEditSave={handleEditSave}
        isDeleting={isDeleting}
      />
    </SegmentPageLayout>
  );
};
